import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Globe, MapPin, Briefcase, Building2, Image as ImageIcon, Mail, Phone, Send, Newspaper, ExternalLink } from "lucide-react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { API } from "../lib/auth";
import JobCard from "../components/JobCard";
import { useI18n } from "../lib/i18n";
import { useSeo } from "../lib/useSeo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicCareerPage() {
  const { employerId } = useParams(); // may be a UUID or a slug — backend resolves both
  const { lang } = useI18n();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ job_id: "", name: "", email: "", phone: "", cover_letter: "", salary_expectation: "" });
  const [cv, setCv] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    axios.get(`${API}/career/${employerId}`).then((r) => setData(r.data)).catch(() => setData({ employer: null }));
  }, [employerId]);

  const tr = (en, fr) => (lang === "fr" ? fr : en);

  useSeo({
    title: data?.employer?.company_name ? `${data.employer.company_name} — Careers | Motravay` : "Career page — Motravay",
    description: data?.page?.about ? data.page.about.slice(0, 160) : tr("Discover the latest job openings and apply directly.", "Découvrez les offres d'emploi et postulez directement."),
    canonical: `/career/${employerId}`,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.job_id) { toast.error(tr("Please pick the job you're applying for.", "Choisissez le poste pour lequel vous postulez.")); return; }
    if (!form.name.trim() || !form.email.trim()) { toast.error(tr("Name and email are required.", "Nom et email obligatoires.")); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("job_id", form.job_id);
      fd.append("name", form.name);
      fd.append("email", form.email);
      if (form.phone) fd.append("phone", form.phone);
      if (form.cover_letter) fd.append("cover_letter", form.cover_letter);
      if (form.salary_expectation) fd.append("salary_expectation", form.salary_expectation);
      if (cv) fd.append("cv", cv);
      await axios.post(`${API}/career/${employerId}/apply`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSubmitted(true);
      toast.success(tr("Application sent — good luck!", "Candidature envoyée — bonne chance !"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || tr("Could not send application. Please retry.", "Envoi impossible. Réessayez."));
    } finally {
      setSubmitting(false);
    }
  };

  const fullUrl = (u) => (u?.startsWith("http") ? u : `${BACKEND_URL}${u}`);
  const company = data?.employer?.company_name;

  // Pre-select a job when only one is open, to make the form feel pre-filled
  useEffect(() => {
    if (data?.jobs?.length === 1 && !form.job_id) setForm((f) => ({ ...f, job_id: data.jobs[0].id }));
    // eslint-disable-next-line
  }, [data]);

  if (!data) return <div className="p-12 text-center text-slate-500">Loading…</div>;
  if (!data.employer) return <div className="p-12 text-center text-slate-500">Employer not found</div>;

  const page = data.page || {};
  const news = data.news || [];

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-4rem)]" data-testid="public-career-page">
      {/* Cover */}
      <div className="relative h-56 sm:h-72 bg-slate-200">
        {page.cover_url ? (
          <img src={fullUrl(page.cover_url)} alt={`${company} cover`} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-slate-200 via-slate-100 to-white flex items-center justify-center text-slate-300"><ImageIcon className="h-10 w-10" /></div>
        )}
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {/* Header card */}
        <Card className="-mt-12 relative z-10 p-6">
          <div className="flex flex-col sm:flex-row gap-5 items-start">
            <div className="h-24 w-24 rounded-2xl border-4 border-white bg-white shadow overflow-hidden -mt-12 sm:mt-0 shrink-0">
              {page.logo_url ? (
                <img src={fullUrl(page.logo_url)} alt={`${company} logo`} className="w-full h-full object-contain" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-400"><Building2 className="h-7 w-7" /></div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="font-display text-3xl font-bold text-slate-900" data-testid="career-company-name">{company}</h1>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
                {page.industry && <span className="inline-flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" />{page.industry}</span>}
                {page.headquarters && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{page.headquarters}</span>}
                {page.website && <a href={page.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#1A8FE3] hover:underline"><Globe className="h-3.5 w-3.5" />{page.website.replace(/^https?:\/\//, "")}</a>}
              </div>
            </div>
          </div>
          {page.about && <p className="text-slate-700 leading-relaxed mt-6 whitespace-pre-line">{page.about}</p>}
        </Card>

        {/* Contact + News grid */}
        {(page.contact_email || page.contact_phone || page.address || news.length > 0) && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
            {(page.contact_email || page.contact_phone || page.address) && (
              <Card className="p-5 lg:col-span-1" data-testid="career-contact-card">
                <h3 className="font-display text-lg font-semibold text-slate-900 mb-3">{tr("Contact", "Contact")}</h3>
                <div className="space-y-2 text-sm text-slate-700">
                  {page.contact_email && <p className="inline-flex items-center gap-2"><Mail className="h-4 w-4 text-[#1A8FE3]" /><a href={`mailto:${page.contact_email}`} className="hover:underline">{page.contact_email}</a></p>}
                  {page.contact_phone && <p className="inline-flex items-center gap-2"><Phone className="h-4 w-4 text-[#1A8FE3]" /><a href={`tel:${page.contact_phone}`} className="hover:underline">{page.contact_phone}</a></p>}
                  {page.address && <p className="inline-flex items-start gap-2"><MapPin className="h-4 w-4 text-[#1A8FE3] mt-0.5" /><span className="whitespace-pre-line">{page.address}</span></p>}
                </div>
              </Card>
            )}
            {news.length > 0 && (
              <Card className="p-5 lg:col-span-2" data-testid="career-news-card">
                <h3 className="font-display text-lg font-semibold text-slate-900 mb-3 inline-flex items-center gap-2"><Newspaper className="h-4 w-4 text-[#1A8FE3]" />{tr("Company news", "Actualités")}</h3>
                <div className="space-y-4">
                  {news.slice(0, 5).map((n) => (
                    <div key={n.id} data-testid={`career-news-${n.id}`} className="border-b border-slate-100 pb-3 last:border-0 last:pb-0">
                      <div className="flex gap-3">
                        {n.image_url && <img src={n.image_url.startsWith("http") ? n.image_url : `${BACKEND_URL}${n.image_url}`} alt={n.title} className="h-16 w-16 object-cover rounded-lg shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-slate-900">{n.title}</h4>
                          <p className="text-sm text-slate-600 mt-1 line-clamp-3 whitespace-pre-line">{n.content}</p>
                          {n.link_url && <a href={n.link_url} target="_blank" rel="noreferrer" className="text-xs text-[#1A8FE3] hover:underline inline-flex items-center gap-1 mt-1"><ExternalLink className="h-3 w-3" />{tr("Read more", "Lire la suite")}</a>}
                          <p className="text-xs text-slate-400 mt-1">{new Date(n.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Unified application form (job dropdown + expected salary) — placed ABOVE open positions */}
        {data.jobs.length > 0 && (
          <div className="pt-10" data-testid="career-apply-section">
            <Card className="p-6 md:p-8">
              {submitted ? (
                <div className="text-center py-6" data-testid="career-apply-success">
                  <h3 className="font-display text-2xl font-bold text-slate-900">{tr("Application received", "Candidature reçue")}</h3>
                  <p className="mt-2 text-slate-600">{tr("Thanks — the recruiter will be in touch soon.", "Merci — le recruteur vous recontactera prochainement.")}</p>
                </div>
              ) : (
                <>
                  <h3 className="font-display text-2xl font-bold text-slate-900">{tr("Apply directly", "Postuler directement")}</h3>
                  <p className="mt-2 text-slate-600">{tr(`Choose which ${company} role you're applying for and we'll route your application to the right team.`, `Choisissez le poste ${company} qui vous intéresse et nous transmettons votre candidature à la bonne équipe.`)}</p>
                  <form className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6" onSubmit={handleSubmit} data-testid="career-apply-form">
                    <div className="md:col-span-2">
                      <Label htmlFor="ca-job">{tr("I'm applying for *", "Je postule pour *")}</Label>
                      <Select value={form.job_id} onValueChange={(v) => setForm({ ...form, job_id: v })}>
                        <SelectTrigger id="ca-job" data-testid="career-apply-job"><SelectValue placeholder={tr("Select a job…", "Choisissez un poste…")} /></SelectTrigger>
                        <SelectContent>
                          {data.jobs.map((j) => (
                            <SelectItem key={j.id} value={j.id} data-testid={`career-apply-job-${j.id}`}>{j.title}{j.location ? ` — ${j.location}` : ""}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="ca-name">{tr("Full name *", "Nom complet *")}</Label>
                      <Input id="ca-name" data-testid="career-apply-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                    </div>
                    <div>
                      <Label htmlFor="ca-email">{tr("Email *", "Email *")}</Label>
                      <Input id="ca-email" data-testid="career-apply-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
                    </div>
                    <div>
                      <Label htmlFor="ca-phone">{tr("Phone", "Téléphone")}</Label>
                      <Input id="ca-phone" data-testid="career-apply-phone" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+230 ..." />
                    </div>
                    <div>
                      <Label htmlFor="ca-salary">{tr("Salary expectations", "Prétentions salariales")}</Label>
                      <Input id="ca-salary" data-testid="career-apply-salary" value={form.salary_expectation} onChange={(e) => setForm({ ...form, salary_expectation: e.target.value })} placeholder={tr("e.g. Rs 35,000 / month or negotiable", "ex. Rs 35 000 / mois ou négociable")} />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="ca-cv">{tr("CV (PDF/DOC) — optional", "CV (PDF/DOC) — optionnel")}</Label>
                      <Input id="ca-cv" data-testid="career-apply-cv" type="file" accept=".pdf,.doc,.docx" onChange={(e) => setCv(e.target.files?.[0] || null)} />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="ca-cover">{tr("What are your motivations to apply for this job?", "Quelles sont vos motivations pour postuler à ce poste ?")}</Label>
                      <Textarea id="ca-cover" data-testid="career-apply-motivations" rows={4} value={form.cover_letter} onChange={(e) => setForm({ ...form, cover_letter: e.target.value })} placeholder={tr("Tell the employer why you're interested and what you'd bring.", "Expliquez au recruteur pourquoi ce poste vous intéresse.")} />
                    </div>
                    <div className="md:col-span-2">
                      <Button type="submit" disabled={submitting} className="rounded-full bg-[#1A8FE3] hover:bg-[#157AC0] text-white px-8 h-11" data-testid="career-apply-submit">
                        {submitting ? tr("Sending…", "Envoi…") : (<><Send className="h-4 w-4 mr-2" />{tr("Send application", "Envoyer ma candidature")}</>)}
                      </Button>
                    </div>
                  </form>
                </>
              )}
            </Card>
          </div>
        )}

        {/* Open positions */}
        <div className="pt-10">
          <h2 className="font-display text-2xl font-bold text-slate-900 mb-5">{tr("Open positions", "Postes ouverts")} ({data.jobs.length})</h2>
          {data.jobs.length === 0 ? (
            <Card className="p-10 text-center text-slate-500">{tr("No open positions at the moment.", "Aucun poste ouvert pour le moment.")}</Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data.jobs.map((j) => <JobCard key={j.id} job={j} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
